/**
 * 1 プロジェクトぶんのタスクボード群（タブ + 表本体）。
 *
 * プロジェクトは複数枚のボードを持てる（例:「制作」「宣伝」）。
 * ここで `GET /api/{projectId}/task-boards` を 1 回だけ叩き、
 *  - タブ（ボードの切り替え・追加・リネーム・並び替え・削除）
 *  - 表（TaskBoardSection）。通常は選択中の 1 枚、フィルター中は全ボード
 *  - 横断ガント / 担当者フィルター用の全ボード報告
 * をまとめて面倒を見る。TaskBoardSection には取得済みのボードを渡すので、
 * 表側で二重に取得しない。
 *
 * タブは作品登録のフィルタータグ管理バーと同じ `FilterChipBar` で描く
 * （クリックで切り替え / ダブルクリックでリネーム / D&D で並び替え /
 * × で削除 / + で追加）。ボードが 1 枚で編集権限も無い場合はバーを出さない。
 *
 * フィルター（担当者 / タスク名 / 日付チップ）適用中はタブを出さず、全ボードを
 * 縦に並べる（複数ボードのときはヘッダーにボード名を添える）。ヒット 0 件の
 * ボードは TaskBoardSection 側で畳まれるため、全ボードが 0 件ならラッパーが
 * 空になり、親（CrossProjectTaskBoard の SectionsWrapper）がプロジェクトごと畳む。
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";

import ConfirmModal from "../../../../components/modal/ConfirmModal";
import FilterChipBar from "../../../../components/filter-chip/FilterChipBar";
import SvgLoadingAnimation from "../../../../components/loading";
import {
  useCreateTaskBoardMutation,
  useDeleteTaskBoardMutation,
  useGetTaskBoardsQuery,
  useReorderTaskBoardsMutation,
  useUpdateTaskBoardMutation,
  type TaskBoard,
} from "../../../../app/services/taskBoard";
import { useMessengerSubscribe } from "../../../../app/messenger/MessengerSocketContext";
import type { MessengerIncomingEvent } from "../../../../types/messengerEvents";
import {
  collectBoardMentions,
  isTaskComplete,
  isTaskFilterActive,
  matchesCompletionFilter,
  taskHasDateInRange,
  type TaskCompletionFilter,
  type TaskDateRange,
} from "./taskBoardUtils";
import TaskBoardSection from "./TaskBoardSection";
import { useTaskBoardMedia } from "./useTaskBoardMedia";
import { useTaskBoardSaveStatus } from "./useTaskBoardSaveStatus";
import { EmbeddedMediaContext } from "../../../../components/markdown/embeddedMedia";
import {
  getAltArrowDirection,
  isTypingTarget,
} from "../../../../utils/keyboardShortcuts";

/**
 * リアルタイム配信（task_board.updated）が届かない場合の保険ポーリング間隔。
 * WS 切断中・旧サーバー相手でも最大この間隔で他ユーザーの更新へ追従する。
 */
const FALLBACK_POLLING_MS = 60_000;

/** BE が `max_boards` を返さない（旧 BE の）場合に仮定するボード数上限。 */
const DEFAULT_MAX_BOARDS = 30;

export interface ProjectTaskBoardsProps {
  projectId: string;
  projectName: string;
  bannerColor?: string;
  /** プロジェクト単位の編集権限（一覧 API の `task_board_access === "edit"`）。 */
  canEdit: boolean;
  stackParallel?: boolean;
  /** 並列工程の縦積み ⇄ 横並びを切り替える（表ラベル行の 3 点リーダーメニュー用） */
  onToggleStackParallel?: () => void;
  /** 完了フィルター（横断ビューの一括切り替え由来）。既定は未完了のみ */
  completionFilter?: TaskCompletionFilter;
  selectedMentions: string[];
  taskNameFilter?: string;
  /** 日付範囲フィルター（日付チップ由来）。null なら絞り込みなし */
  dateRange?: TaskDateRange | null;
  /** `@表示名` → チェックボックス色（アクティビティ集計のユーザー配色） */
  // eslint-disable-next-line no-unused-vars
  mentionColorOf?: (name: string) => string | undefined;
  /** 列（工程）名 → 色（アクティビティ集計の項目配色）。列ラベルの背景に使う */
  // eslint-disable-next-line no-unused-vars
  columnColorOf?: (columnTitle: string) => string | undefined;
  /** コードネーム絞り込みで対象外のときに畳む（データ供給は継続）。 */
  hidden?: boolean;
  /** 自分のポジション（一覧 API の `current_user_role` を渡す）。見出しの導線アイコン用 */
  position: string;
  /** 納品物なし（total_shots_count === 0）。導線アイコンにホームを出さない */
  noDeliverable: boolean;
  /** このプロジェクトのボード本文 @メンションを親へ報告する。 */
  onReportMentions: (
    // eslint-disable-next-line no-unused-vars
    projectId: string,
    // eslint-disable-next-line no-unused-vars
    mentions: string[],
  ) => void;
  /** 横断ガント用に、このプロジェクトの全ボードを親へ報告する。 */
  onReportBoards: (
    // eslint-disable-next-line no-unused-vars
    projectId: string,
    // eslint-disable-next-line no-unused-vars
    boards: TaskBoard[] | null,
  ) => void;
  onSaveStart?: () => void;
  // eslint-disable-next-line no-unused-vars
  onSaveEnd?: (ok: boolean) => void;
  /**
   * ⌥←/→（ボードタブの切り替え）の対象プロジェクトか。横断ビューでは
   * 「最後に操作したプロジェクト」1 つだけが true になる（親が決める）。
   */
  isShortcutTarget?: boolean;
  /** このセクションが操作された（ポインター・フォーカス）ことを親へ報告する */
  // eslint-disable-next-line no-unused-vars
  onShortcutFocus?: (projectId: string) => void;
}

const ProjectTaskBoards: React.FC<ProjectTaskBoardsProps> = ({
  projectId,
  projectName,
  bannerColor,
  canEdit,
  stackParallel,
  onToggleStackParallel,
  completionFilter = "incomplete",
  selectedMentions,
  taskNameFilter,
  dateRange = null,
  mentionColorOf,
  columnColorOf,
  hidden = false,
  position,
  noDeliverable,
  onReportMentions,
  onReportBoards,
  onSaveStart,
  onSaveEnd,
  isShortcutTarget = false,
  onShortcutFocus,
}) => {
  const { data, isLoading, isFetching, refetch, fulfilledTimeStamp } =
    useGetTaskBoardsQuery(
      { project_id: projectId },
      {
        skip: !projectId,
        // 他端末や別タブの更新に追従する（表側と同じ方針）。
        refetchOnFocus: true,
        refetchOnReconnect: true,
        // WS 配信（task_board.updated）の取りこぼし・切断への保険。
        // 非フォーカスタブでは止め、復帰時は refetchOnFocus が拾う。
        pollingInterval: FALLBACK_POLLING_MS,
        skipPollingIfUnfocused: true,
      },
    );

  // 他ユーザーの変更通知（task_board.updated）で再取得する。送信者本人は
  // BE 側で除外されるため、自分の操作で二重取得にはならない。取得結果は
  // TaskBoardSection の「内容ハッシュキー比較マージ」でローカル編集と共存する。
  const handleTaskBoardEvent = useCallback(
    (ev: MessengerIncomingEvent) => {
      if (ev.type === "task_board.updated") refetch();
    },
    [refetch],
  );
  useMessengerSubscribe(
    "task_board",
    projectId || undefined,
    handleTaskBoardEvent,
  );

  // プロジェクト単位の保存状態（表の右下の同期バーに表示）。
  // 親（横断ビュー）の画面右下固定インジケーターへも従来どおり伝搬する。
  const {
    status: localSaveStatus,
    onSaveStart: onLocalSaveStart,
    onSaveEnd: onLocalSaveEnd,
  } = useTaskBoardSaveStatus();
  const handleSaveStart = useCallback(() => {
    onLocalSaveStart();
    onSaveStart?.();
  }, [onLocalSaveStart, onSaveStart]);
  const handleSaveEnd = useCallback(
    (ok: boolean) => {
      onLocalSaveEnd(ok);
      onSaveEnd?.(ok);
    },
    [onLocalSaveEnd, onSaveEnd],
  );

  const [createBoard, { isLoading: isCreatingBoard }] =
    useCreateTaskBoardMutation();
  const [updateBoard] = useUpdateTaskBoardMutation();
  const [reorderBoards] = useReorderTaskBoardsMutation();
  const [deleteBoard, { isLoading: isDeletingBoard }] =
    useDeleteTaskBoardMutation();

  const boards = useMemo(() => data?.data?.boards ?? [], [data]);

  // セル添付のサムネイル・最適化画像（ボード本体とは別に 1 回でまとめて引く）
  const resolveEmbeddedMedia = useTaskBoardMedia(projectId, boards);

  // ボード数の上限（BE の MAX_BOARDS_PER_PROJECT）。上限に達したら「+」チップを
  // 出さず、万一すり抜けて 400 が返ったときは理由をトーストで伝える。
  // 旧 BE は max_boards を返さないため「+」の出し分けは既定値で行い、トーストの
  // 具体的な枚数は BE が返したときだけ出す（実際の上限と食い違わないように）。
  const reportedMaxBoards = data?.data?.max_boards;
  const maxBoards = reportedMaxBoards ?? DEFAULT_MAX_BOARDS;

  const [activeBoardId, setActiveBoardId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TaskBoard | null>(null);

  // 選択中ボードが消えた（他端末での削除など）ときは先頭に戻す。
  const activeBoard = useMemo(
    () => boards.find((b) => b.id === activeBoardId) ?? boards[0],
    [boards, activeBoardId],
  );

  useEffect(() => {
    onReportBoards(projectId, boards.length > 0 ? boards : null);
  }, [projectId, boards, onReportBoards]);

  // アンマウント時（プロジェクトが一覧から外れた等）は登録を解除する。
  useEffect(
    () => () => onReportBoards(projectId, null),
    [projectId, onReportBoards],
  );

  // 担当者チップの母集合は全ボードを合わせて作る（タブを切り替えなくても
  // 他ボードの担当者で絞り込めるように）。
  // 完了フィルターと同じ視点（既定 = 未完了タスクのみ）のタスクからのみ収集し、
  // 「未対応タスクがない担当者」のチップが並ばないようにする。
  const mentions = useMemo(() => {
    const result: string[] = [];
    for (const board of boards) {
      const source = board.tasks.filter(
        (task) =>
          matchesCompletionFilter(
            isTaskComplete(task, board.columns),
            completionFilter,
          ) &&
          // 日付チップで絞り込み中は、その期間にタスクを持つ担当者だけ並べる
          (!dateRange || taskHasDateInRange(task, dateRange)),
      );
      for (const name of collectBoardMentions(source)) {
        if (!result.includes(name)) result.push(name);
      }
    }
    return result;
  }, [boards, completionFilter, dateRange]);

  useEffect(() => {
    onReportMentions(projectId, mentions);
  }, [projectId, mentions, onReportMentions]);

  const chipItems = useMemo(
    () => boards.map((board) => ({ id: board.id, name: board.name })),
    [boards],
  );

  // 行メニュー「行を移動」の移動先候補（移動元を除く、編集できるボードのみ）。
  // 列タイトルの事前照合に使うため columns も渡す。
  const moveTargetBoardsFor = useCallback(
    (sourceBoardId: string) =>
      boards
        .filter((b) => b.id !== sourceBoardId && b.access !== "view")
        .map((b) => ({ id: b.id, name: b.name, columns: b.columns })),
    [boards],
  );

  const handleAddBoard = useCallback(
    async (name: string) => {
      if (isCreatingBoard) return;
      handleSaveStart();
      try {
        const res = await createBoard({ project_id: projectId, name }).unwrap();
        handleSaveEnd(true);
        setActiveBoardId(res.data.board.id);
      } catch (err) {
        handleSaveEnd(false);
        // 400 は上限到達（他端末で同時に追加された等で「+」が残っていた場合）。
        // 理由が分からない「失敗しました」だけにならないよう上限を案内する。
        if ((err as { status?: number })?.status === 400) {
          toast.error(
            reportedMaxBoards
              ? `ボードは1プロジェクト${reportedMaxBoards}枚まで追加できます`
              : "ボード数が上限に達しています",
          );
        } else {
          toast.error("ボードの追加に失敗しました");
        }
      }
    },
    [
      createBoard,
      isCreatingBoard,
      projectId,
      reportedMaxBoards,
      handleSaveStart,
      handleSaveEnd,
    ],
  );

  const handleRenameBoard = useCallback(
    async (item: { id: string }, name: string) => {
      handleSaveStart();
      try {
        await updateBoard({
          project_id: projectId,
          board_id: item.id,
          name,
        }).unwrap();
        handleSaveEnd(true);
      } catch {
        handleSaveEnd(false);
        toast.error("ボード名の変更に失敗しました");
      }
    },
    [updateBoard, projectId, handleSaveStart, handleSaveEnd],
  );

  const handleReorderBoards = useCallback(
    async (reordered: { id: string }[]) => {
      handleSaveStart();
      try {
        await reorderBoards({
          project_id: projectId,
          board_ids: reordered.map((item) => item.id),
        }).unwrap();
        handleSaveEnd(true);
      } catch {
        handleSaveEnd(false);
        toast.error("ボードの並び替えに失敗しました");
      }
    },
    [reorderBoards, projectId, handleSaveStart, handleSaveEnd],
  );

  const handleDeleteBoard = useCallback(async () => {
    const target = deleteTarget;
    setDeleteTarget(null);
    if (!target) return;
    handleSaveStart();
    try {
      await deleteBoard({
        project_id: projectId,
        board_id: target.id,
      }).unwrap();
      handleSaveEnd(true);
      setActiveBoardId(null);
    } catch {
      handleSaveEnd(false);
      toast.error("ボードの削除に失敗しました");
    }
  }, [deleteTarget, deleteBoard, projectId, handleSaveStart, handleSaveEnd]);

  // フィルター適用中はタブ・追加ボタンを出さず、ヒットしたボードを全て並べる
  // （判定は TaskBoardSection の「編集系の抑制」と同じ定義）。
  // ⌥←/→ の無効化判定にも使うため、早期 return より前で計算する。
  const filtering = isTaskFilterActive({
    selectedMentions,
    taskNameFilter,
    dateRange,
  });

  // ── ⌥←/→ による表示ボード（タブ）の循環切り替え ──
  // 対象は「最後に操作したプロジェクト」（isShortcutTarget。横断ビューで親が
  // 1 つだけ選ぶ）。セル入力中・フィルター中（タブ非表示）・ボード 1 枚以下の
  // ときは何もしない。macOS 既定の ⌥←/→（単語移動）は入力中のみの挙動なので、
  // isTypingTarget の除外と衝突しない。
  const currentBoardId = activeBoard?.id;
  useEffect(() => {
    if (!isShortcutTarget || hidden || filtering || boards.length <= 1) return;
    const handler = (e: KeyboardEvent) => {
      const direction = getAltArrowDirection(e);
      if (direction === null || isTypingTarget(e.target)) return;
      e.preventDefault();
      const index = boards.findIndex((b) => b.id === currentBoardId);
      const next =
        boards[
          (Math.max(index, 0) + direction + boards.length) % boards.length
        ];
      if (next) setActiveBoardId(next.id);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isShortcutTarget, hidden, filtering, boards, currentBoardId]);

  // このセクションへの操作（クリック・ホバー・フォーカス）を親へ報告し、
  // ⌥←/→ の対象プロジェクトを「最後に操作したプロジェクト」に保つ。
  const reportShortcutFocus = useCallback(
    () => onShortcutFocus?.(projectId),
    [onShortcutFocus, projectId],
  );

  if (hidden) return null;

  // 初回取得中はタブも表も出せないので、表側と同じ位置でローディングを見せる。
  if (!activeBoard) {
    return isLoading ? (
      <div className="u-col-center u-w100 u-ptb16">
        <SvgLoadingAnimation />
      </div>
    ) : null;
  }

  const renderSection = (board: TaskBoard, boardLabel?: string) => (
    <TaskBoardSection
      // ボード単位で表のローカル state を持つ（切り替えで作り直す）
      key={board.id}
      projectId={projectId}
      projectName={projectName}
      boardLabel={boardLabel}
      board={board}
      boardId={board.id}
      onRefetchBoard={refetch}
      isRefreshing={isFetching}
      bannerColor={bannerColor}
      // ボード単位の共有設定で閲覧のみになることがある
      canEdit={canEdit && board.access !== "view"}
      // やることリスト（チェックバック中ショット）の自動行追加は一番左の
      // ボードだけに入れる。ボードを分けたときに同じ行が各ボードへ増える
      // のを防ぐため。
      enableShotAutoAdd={board.id === boards[0]?.id}
      stackParallel={stackParallel}
      onToggleStackParallel={onToggleStackParallel}
      completionFilter={completionFilter}
      selectedMentions={selectedMentions}
      taskNameFilter={taskNameFilter}
      dateRange={dateRange}
      mentionColorOf={mentionColorOf}
      columnColorOf={columnColorOf}
      position={position}
      noDeliverable={noDeliverable}
      onSaveStart={handleSaveStart}
      onSaveEnd={handleSaveEnd}
      lastSyncedAt={fulfilledTimeStamp}
      saveStatus={localSaveStatus}
      moveTargetBoards={moveTargetBoardsFor(board.id)}
    />
  );

  // 表示するボード。フィルター中は全ボード（ヒット 0 件のボードは Section 自身が
  // null を返す）、通常時は選択中の 1 枚。常に配列で描いて key を揃え、フィルターの
  // ON/OFF で開いていたボードの表が作り直されないようにする。
  // 全ボードが 0 件で中身が空になったラッパーは親（SectionsWrapper）が畳む。
  const visibleBoards = filtering ? boards : [activeBoard];
  // ボードが複数あるプロジェクトだけ、見分け用にボード名を添える。
  const showBoardLabel = filtering && boards.length > 1;

  // ボードが 1 枚しか無く追加もできないなら、タブ行は出さずに従来の見た目にする。
  // フィルター中は切り替え・追加の代わりにヒットしたボードを全て並べるので出さない。
  const showTabs = !filtering && (boards.length > 1 || canEdit);
  // 上限（BE の MAX_BOARDS_PER_PROJECT）に達したら「+」チップを出さない。
  const canAddBoard = canEdit && boards.length < maxBoards;
  // ボードは 1 枚残す必要がある（BE も最後の 1 枚は削除を拒否する）。
  const canDeleteBoard = canEdit && boards.length > 1;
  // 並び替えは 2 枚以上のときだけ意味がある。
  const canReorderBoards = canEdit && boards.length > 1;

  return (
    <div
      className="u-col u-gap8 u-w100"
      onPointerDownCapture={reportShortcutFocus}
      onPointerEnter={reportShortcutFocus}
      onFocusCapture={reportShortcutFocus}
    >
      {showTabs && (
        <FilterChipBar
          items={chipItems}
          selectedId={activeBoard.id}
          onSelect={(id) => id && setActiveBoardId(id)}
          // 常にどれか 1 枚のボードを開くので「すべて」チップは出さない
          showAllChip={false}
          wrap
          addPlaceholder="新しいボード"
          onAdd={canAddBoard ? handleAddBoard : undefined}
          onRename={canEdit ? handleRenameBoard : undefined}
          onReorder={canReorderBoards ? handleReorderBoards : undefined}
          onRemove={
            canDeleteBoard
              ? (item) =>
                  setDeleteTarget(
                    boards.find((b) => b.id === item.id) ?? null,
                  )
              : undefined
          }
        />
      )}

      <EmbeddedMediaContext.Provider value={resolveEmbeddedMedia}>
        {visibleBoards.map((board) =>
          renderSection(board, showBoardLabel ? board.name : undefined),
        )}
      </EmbeddedMediaContext.Provider>

      {deleteTarget && (
        <ConfirmModal
          message={`「${deleteTarget.name}」を削除しますか？ボード内のタスクもすべて削除されます。`}
          confirmLabel="削除"
          onConfirm={handleDeleteBoard}
          onCancel={() => setDeleteTarget(null)}
          loading={isDeletingBoard}
        />
      )}
    </div>
  );
};

export default ProjectTaskBoards;
